import { compact } from 'lodash';

import { normalizeViewPhoneNumber } from 'utils/data';

import { ASSIGNED_LOAN_OFFICER_PREFIX } from './RealtorPicker.constants';

const getRealtorFullName = ({ firstName, lastName }) => compact([firstName, lastName]).join(' ');

const getRealtorOptionLabel = realtor => {
  const fullName = getRealtorFullName(realtor);
  const email = realtor.emailAddress ?? realtor.primaryEmail ?? '';

  return email ? `${fullName} — ${email}` : fullName;
};

// Surface the realtor's owning LO so the picking LO can see at a glance that
// the realtor is already someone else's portal partner (anti-poaching).
const getAssignedLoanOfficerLabel = assignedLoanOfficer => {
  const assignedName = assignedLoanOfficer ? getRealtorFullName(assignedLoanOfficer) : '';

  return assignedName ? `${ASSIGNED_LOAN_OFFICER_PREFIX} ${assignedName}` : '';
};

/**
 * Map realtor-directory entries to autocomplete options.
 *
 * @param {Array} realtors - directory realtor objects
 * @returns {Array} option objects
 */
export const getRealtorOptions = realtors =>
  realtors.map(({ id, firstName, lastName, emailAddress, phoneNumber, assignedLoanOfficer }) => {
    const realtor = { id, firstName, lastName, emailAddress, phoneNumber };

    return {
      name: getRealtorOptionLabel(realtor),
      value: id,
      realtor,
      assignedLoanOfficerLabel: getAssignedLoanOfficerLabel(assignedLoanOfficer),
    };
  });

/**
 * Map CRM agent-contacts (tagged "Realtor Agent") to autocomplete options.
 * The realtor object carries the license/office fields for prefill.
 *
 * @param {Array} contacts - CRM contact objects
 * @returns {Array} option objects
 */
export const getCrmAgentOptions = contacts =>
  contacts.map(contact => {
    const id = contact?.id?.value ?? contact?.id ?? null;
    const firstName = contact.firstName ?? '';
    const lastName = contact.lastName ?? '';
    const emailAddress = contact.primaryEmail ?? contact.email ?? '';
    const phoneNumber = contact.primaryPhone ?? contact.phone ?? '';

    const realtor = {
      id,
      firstName,
      lastName,
      emailAddress,
      phoneNumber,
      // License fields from CRM — not present on directory realtors.
      licenseNumber: contact.licenseNumber ?? '',
      stateLicenseId: contact.stateLicenseId ?? '',
      office: contact.office ?? '',
      fromCrm: true,
    };

    return {
      name: getRealtorOptionLabel(realtor),
      value: `crm-${id}`,
      realtor,
      assignedLoanOfficerLabel: '',
    };
  });

/**
 * Merge realtor-directory options and CRM agent options, de-duplicating by
 * email (prefer the directory entry when both sources have the same email).
 *
 * @param {Array} directoryOptions - from getRealtorOptions
 * @param {Array} crmOptions - from getCrmAgentOptions
 * @returns {Array} merged options
 */
export const mergeRealtorOptions = (directoryOptions, crmOptions) => {
  const directoryEmails = new Set(
    directoryOptions.map(o => (o.realtor?.emailAddress ?? '').trim().toLowerCase()).filter(Boolean),
  );

  const deduplicatedCrmOptions = crmOptions.filter(o => {
    const email = (o.realtor?.emailAddress ?? '').trim().toLowerCase();

    return !email || !directoryEmails.has(email);
  });

  return [...directoryOptions, ...deduplicatedCrmOptions];
};

/**
 * Produce the form prefill values from the selected realtor option.
 * For CRM-sourced options (fromCrm: true) the license/office fields are
 * also returned since the form already handles them via setInForm.
 *
 * @param {object} realtor - the realtor object from the selected option
 * @returns {object} keyed by nameInModel fields in AgentAndSeller.config.js
 */
export const getRealtorPrefillValues = realtor => {
  const base = {
    name: getRealtorFullName(realtor),
    email: realtor.emailAddress ?? '',
    phone: normalizeViewPhoneNumber(realtor.phoneNumber) ?? '',
  };

  if (!realtor.fromCrm) {
    return base;
  }

  return {
    ...base,
    agentNmlsId: realtor.licenseNumber ?? '',
    agentStateLicenseId: realtor.stateLicenseId ?? '',
    office: realtor.office ?? '',
  };
};
